import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthStore } from './auth.store';
import { AuthFacade } from './auth.facade';
import { AuthService, User } from '@auth0/auth0-angular';
import { ILinkAccountAPI, ILinkAccountState, TAuthProvider } from './auth.interface';
import { catchError, filter, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoggingService } from '../utils/logging.service';
// import { ROUTER_TOKENS } from '../../app.routes';

const AUTH0_DOMAIN = import.meta.env.NG_APP_AUTH0_DOMAIN;
const API_DOMAIN = import.meta.env.NG_APP_API_URL;
const AUTH0_REDIRECT_URI = import.meta.env.NG_APP_AUTH0_REDIRECT_URI;

@Injectable({
  providedIn: 'root',
})
export class AuthzService {
  protected readonly authStore = inject(AuthStore);
  private readonly authFacade = inject(AuthFacade);
  private readonly auth0 = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly loggingService = inject(LoggingService);

  constructor() {
    this.loggingService.error('🪅AuthzService');
    // this.getUserFromAuth0().pipe(
    //   tap(() => this.loggingService.log('🪅AuthzService[OBS]'))
    // ).subscribe()
  }

  setLinkAccountState(newState: ILinkAccountState) {
    this.authStore.setLinkAccountState(newState);
  }

  get linkAccountState(): ILinkAccountState {
    return this.authStore.linkAccountState();
  }

  /**
   * Login to Auth0
   * - callback to handleRedirectCallback()
   */
  login() {
    // const redirectUrl = `${window.location.origin}/${ROUTER_TOKENS.AUTH_CALLBACK}`
    this.loggingService.error('###login', AUTH0_REDIRECT_URI);
    this.auth0.loginWithRedirect({
      authorizationParams: {
        scope: 'openid profile email', // update:users update:current_user_identities
        redirect_uri: AUTH0_REDIRECT_URI,
      },
      appState: {
        target: `/auth-callback`,
      },
    });
  }

  /**
   * Callback from Auth0
   * - Save user state
   */
  handleRedirectCallback(): Observable<User | null> {
    return this.getUserFromAuth0();
  }

  getUserFromAuth0(): Observable<User | null> {
    this.loggingService.log('getUserFromAuth!!!!!!!!!!');
    this.loggingService.log('log---getUserFromAuth0');
    this.loggingService.log('----------------------------');
    return this.auth0.user$.pipe(
      tap((user) => this.loggingService.warn('###getUserFromAuth0=', user)),
      map((user) => user ?? null),
      tap((user) => {
        if (user) {
          this.loggingService.warn('user', user);
          this.authFacade.updateAuthState({
            isLoggedIn: true,
            userRole: user['app_metadata']['role'],
            userState: user['app_metadata']['user_state'],
            authId: user.sub!,
            userId: user['user_metadata']['userId'] || null,
          });
          this.authStore.setUserProfileState({
            firstName: user.given_name,
            lastName: user.family_name,
            nickname: '',
            email: user.email,
            avatarUrl: user.picture,
            mobile: '',
          });
          this.authStore.setUserMetaData(user['user_metadata']);
        }
      }),
      catchError((err) => {
        // ! TODO implement error handling
        this.loggingService.error('handleRedirectCallback', err);
        return throwError(() => err);
      })
    );
  }

  getUserAuth0() {
    this.auth0.user$.subscribe((user) => {
      this.loggingService.warn('app-metadata', user?.['app_metadata']);
      this.loggingService.warn('user-metadata', user?.['user_metadata']);
    });
  }

  getUserMetaDataAuth0() {
    const authUser$ = this.auth0.user$.pipe(
      filter((user): user is NonNullable<typeof user> => user != null),
      tap((user) => this.loggingService.log('🌊getUserMetaDataAuth0', user)),
      map((user) => user['user_metadata'])
    );
    return toSignal(authUser$);
    // return authUser$
  }

  getCurrentUser() {
    return this.auth0.user$.pipe(
      tap((user) => {
        this.loggingService.warn('getCurrentUser', user);
        if (user) {
          this.authFacade.updateAuthState({
            isLoggedIn: true,
            userRole: user['app_metadata']['role'],
            userState: user['app_metadata']['user_state'],
            authId: user.sub!,
            userId: user['user_metadata']['userId'] || null,
          });
          this.authFacade.updateUserState({
            firstName: user.given_name,
            lastName: user.family_name,
            nickname: user.nickname,
            email: user.email,
            avatarUrl: user.picture,
            mobile: '',
          });
        }
      })
    );
  }

  logout() {
    const homepage = `${window.location.origin}`;
    this.authStore.updateAuthState({
      isLoggedIn: false,
      userRole: null,
      authId: null,
      userId: null,
    });
    // this.authStore.isAuth.set(false)
    this.auth0.logout({
      logoutParams: {
        returnTo: homepage,
      },
    });
    this.authStore.clearState();
  }

  linkAccount(userId: string, provider: TAuthProvider) {
    this.loggingService.log('@@@AuthzService-linkAccount');
    const primaryUser = this.linkAccountState.sub;

    const body: ILinkAccountAPI = {
      primaryUserId: primaryUser.sub, // ? == auth0|xxx
      provider: provider,
      secondaryUserId: userId, // ? == x-oauth2|xxx
    };

    this.loggingService.log('@@@AuthzService');
    this.loggingService.log('primaryUserId', body.primaryUserId);
    this.loggingService.log('secondaryUserId', body.secondaryUserId);

    return this.http.post(`${API_DOMAIN}/authentication/link-account`, body, {});
  }

  unlinkAccount(provider: TAuthProvider) {
    this.loggingService.log('@@@AuthzService-unlinkAccount');
    return this.auth0.user$.pipe(
      filter((user): user is NonNullable<typeof user> => user != null),
      switchMap((user) => {
        const body: Partial<ILinkAccountAPI> = {
          primaryUserId: user.sub, // ? == auth0|xxx
          provider: provider,
        };
        return this.http.post(`${API_DOMAIN}/authentication/unlink-account`, body, {});
      }),
      catchError((err) => {
        this.loggingService.error('Error unlink account \n\n', err);
        return throwError(() => err);
      })
    );
  }

  linkAccountWithJWT(targetUserIdToken: string) {
    this.loggingService.error('accessToken', this.linkAccountState.accessToken);
    this.loggingService.error('sub', this.linkAccountState.sub);
    const httpOption = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.linkAccountState.accessToken}`,
      }),
    };
    /*    const httpOption = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${AUTH0_API_TOKEN}`,
          })
        }*/

    /**
     *     POST /api/v2/users/PRIMARY_ACCOUNT_USER_ID/identities
     *       Authorization: "Bearer PRIMARY_ACCOUNT_JWT"
     *       {
     *         "link_with": "SECONDARY_ACCOUNT_JWT"
     *       }
     */
    const primaryUserId = this.linkAccountState.sub['sub'];
    this.loggingService.error('primaryUserId', primaryUserId);

    const body = {
      link_with: targetUserIdToken,
    };

    this.loggingService.log('----body----', body);
    return this.http.post(
      `https://${AUTH0_DOMAIN}/api/v2/users/${primaryUserId}/identities`,
      JSON.stringify(body),
      httpOption
    );
  }
}
